import { Checkbox, Textfield, token } from "@may-i/mash-design-system";
import { type ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { FloorPlanMemoDiagram, FloorPlanMemoText } from "~/_legacy/types/analysis";

/** 색상 팔레트 — 레거시와 비슷한 톤 */
const COLOR_PALETTE = [
  "#FF6B6B",
  "#FF9F43",
  "#FFD43B",
  "#69DB7C",
  "#20C997",
  "#22B8CF",
  "#339AF0",
  "#1E2D5C",
  "#9775FA",
  "#F783AC",
  "#FFFFFF",
  "#11111F",
];

export type SelectedMemo =
  | { kind: "text"; id: number; floorPlanId: number; content: string; style: FloorPlanMemoText["style"] }
  | { kind: "diagram"; id: number; floorPlanId: number; style: FloorPlanMemoDiagram["style"] };

interface Props {
  memo: SelectedMemo;
  onTextStyleChange: (id: number, partial: Partial<FloorPlanMemoText["style"]>) => void;
  onDiagramStyleChange: (id: number, partial: Partial<FloorPlanMemoDiagram["style"]>) => void;
}

export function FloorPlanMemoEditPanel({ memo, onTextStyleChange, onDiagramStyleChange }: Props) {
  const { t } = useTranslation("camera-mapping");

  // input 입력 도중에 빈 문자열 등 임시 invalid 상태도 허용하기 위한 local state
  const [fontSizeStr, setFontSizeStr] = useState("");
  const [opacityStr, setOpacityStr] = useState("");
  useEffect(() => {
    if (memo.kind === "text") setFontSizeStr(String(memo.style.fontSize));
  }, [memo.kind, memo.id, memo.kind === "text" ? memo.style.fontSize : 0]);
  useEffect(() => {
    setOpacityStr(String(Math.round(memo.style.opacity * 100)));
  }, [memo.id, memo.style.opacity]);

  if (memo.kind === "text") {
    const { style, id } = memo;
    return (
      <Panel>
        <Label>T</Label>
        <FieldRow>
          <Label>{t("memoEdit.fontSize")}</Label>
          <FieldInputBox>
            <Textfield
              size="xSmall"
              value={fontSizeStr}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setFontSizeStr(e.target.value);
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v > 0 && v <= 200) onTextStyleChange(id, { fontSize: v });
              }}
            />
          </FieldInputBox>
        </FieldRow>
        <Divider />
        <ColorPalette>
          {COLOR_PALETTE.map((c) => (
            <ColorSwatch
              key={c}
              type="button"
              $color={c}
              $active={style.color.toUpperCase() === c.toUpperCase()}
              onClick={() => onTextStyleChange(id, { color: c })}
              aria-label={c}
            />
          ))}
        </ColorPalette>
        <Divider />
        <FieldRow>
          <Label>{t("memoEdit.opacity")}</Label>
          <FieldInputBox>
            <Textfield
              size="xSmall"
              value={opacityStr}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setOpacityStr(e.target.value);
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 0 && v <= 100) onTextStyleChange(id, { opacity: v / 100 });
              }}
            />
          </FieldInputBox>
        </FieldRow>
        <Divider />
        <Checkbox
          size="small"
          label={t("memoEdit.stroke")}
          checked={style.hasStroke}
          onCheckedChange={(checked) => onTextStyleChange(id, { hasStroke: checked === true })}
        />
      </Panel>
    );
  }

  const { style, id } = memo;
  return (
    <Panel>
      <Label>{t("memoEdit.fill")}</Label>
      <ColorPalette>
        <ColorSwatch
          type="button"
          $color="transparent"
          $active={!style.fill || style.fill === "transparent"}
          onClick={() => onDiagramStyleChange(id, { fill: undefined })}
          aria-label="transparent"
        >
          <TransparentMark />
        </ColorSwatch>
        {COLOR_PALETTE.map((c) => {
          const rgba = hexToRgba(c, 0.24);
          return (
            <ColorSwatch
              key={`fill-${c}`}
              type="button"
              $color={rgba}
              $active={style.fill === rgba}
              onClick={() => onDiagramStyleChange(id, { fill: rgba })}
              aria-label={c}
            />
          );
        })}
      </ColorPalette>
      <Divider />
      <Label>{t("memoEdit.borderColor")}</Label>
      <ColorPalette>
        {COLOR_PALETTE.map((c) => (
          <ColorSwatch
            key={`border-${c}`}
            type="button"
            $color={c}
            $active={(style.borderColor ?? "").toUpperCase() === c.toUpperCase()}
            onClick={() => onDiagramStyleChange(id, { borderColor: c })}
            aria-label={c}
          />
        ))}
      </ColorPalette>
      <Divider />
      <FieldRow>
        <Label>{t("memoEdit.opacity")}</Label>
        <FieldInputBox>
          <Textfield
            size="xSmall"
            value={opacityStr}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setOpacityStr(e.target.value);
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v) && v >= 0 && v <= 100) onDiagramStyleChange(id, { opacity: v / 100 });
            }}
          />
        </FieldInputBox>
      </FieldRow>
    </Panel>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const m = hex.replace("#", "");
  const r = parseInt(m.substring(0, 2), 16);
  const g = parseInt(m.substring(2, 4), 16);
  const b = parseInt(m.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const Panel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  flex-wrap: wrap;
`;

const Label = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${token("colors.text.primary")};
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: ${token("colors.gray.GR300")};
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FieldInputBox = styled.div`
  width: 56px;
`;

const ColorPalette = styled.div`
  display: flex;
  gap: 4px;
`;

const ColorSwatch = styled.button<{ $color: string; $active: boolean }>`
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 4px;
  border: ${({ $active }) => ($active ? "2px solid #11111F" : "1px solid rgba(0,0,0,0.12)")};
  background: ${({ $color }) => $color};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TransparentMark = styled.span`
  width: 14px;
  height: 1px;
  background: #FF6B6B;
  transform: rotate(-45deg);
`;
